import { useState } from "react";
import { MessageSquare, Send } from "lucide-react";
import { addRfpPrimeContactMessage, setRfpPrimeContactReply, type RfpPrimeContact } from "../api/CrmApi";
import { formatDateTime } from "./crmUi";
import ReceivedAtField, { receivedAtIso } from "./ReceivedAtField";
import Modal from "shared/Modal";
import Button from "shared/Button";
import Alert from "shared/Alert";
import { FORM_CONTROL } from "shared/tableStyles";

interface Props {
  contact: RfpPrimeContact;
  onClose: () => void;
  onSaved: (updated: RfpPrimeContact) => void;
}

export default function LogReplyModal({ contact: initialContact, onClose, onSaved }: Props) {
  const [contact, setContact] = useState(initialContact);
  const [sender, setSender] = useState<"contact" | "us">("contact");
  const [text, setText] = useState("");
  const [receivedAt, setReceivedAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function addMessage() {
    if (!text.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await addRfpPrimeContactMessage(contact.id, sender, text.trim(), sender === "contact" ? receivedAtIso(receivedAt) : undefined);
      setContact(updated);
      onSaved(updated);
      setText("");
      setReceivedAt("");
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || "Failed to log message. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function clearRepliedFlag() {
    setSaving(true);
    setError(null);
    try {
      const updated = await setRfpPrimeContactReply(contact.id, false);
      setContact(updated);
      onSaved(updated);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || "Failed to clear reply flag. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  const thread = contact.reply_thread ?? [];
  const chip = (active: boolean) =>
    `px-3 py-1 text-xs rounded-full border transition-colors ${
      active ? "bg-primary-soft border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"
    }`;

  return (
    <Modal
      size="md"
      title="Conversation Log"
      description={contact.org_name}
      icon={<MessageSquare />}
      onClick={saving ? undefined : onClose}
      closeButtonCLick={saving ? undefined : onClose}
      bodyClassName="p-0 flex flex-col"
    >
      {/* Thread */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2.5 min-h-[120px] max-h-[45vh]">
        {thread.length === 0 && (
          <p className="text-xs text-muted-foreground">No messages logged yet.</p>
        )}
        {thread.map((m, i) => (
          <div key={i} className={`flex ${m.sender === "us" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                m.sender === "us"
                  ? "bg-primary-soft text-foreground"
                  : "bg-surface-muted border border-border text-foreground"
              }`}
            >
              <p className="whitespace-pre-wrap break-words">{m.text}</p>
              <p className="text-[10px] text-muted-foreground mt-1">
                {m.sender === "us" ? "Us" : "Them"} · {formatDateTime(m.at)}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Composer */}
      <div className="px-6 py-4 border-t border-border">
        <div className="flex items-center gap-2 mb-2">
          <button onClick={() => setSender("contact")} className={chip(sender === "contact")}>
            They said
          </button>
          <button onClick={() => setSender("us")} className={chip(sender === "us")}>
            We replied
          </button>
          {contact.replied_at && (
            <button
              onClick={clearRepliedFlag}
              disabled={saving}
              className="ml-auto text-[11px] text-destructive/70 hover:text-destructive disabled:opacity-40"
            >
              Clear "replied" flag
            </button>
          )}
        </div>
        <div className="flex items-end gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            placeholder={sender === "contact" ? "What they said..." : "What we replied..."}
            className={`flex-1 px-3 py-2 ${FORM_CONTROL} resize-none`}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && text.trim() && !saving) {
                e.preventDefault();
                addMessage();
              }
            }}
          />
          <Button
            variant="primary"
            size="icon"
            onClick={addMessage}
            disabled={saving || !text.trim()}
            title="Add message"
            className="shrink-0 p-2.5"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        {sender === "contact" && <div className="mt-2"><ReceivedAtField id={`log-received-${contact.id}`} value={receivedAt} onChange={setReceivedAt} disabled={saving} /></div>}
        {error && (
          <Alert tone="danger" className="mt-2">{error}</Alert>
        )}
      </div>
    </Modal>
  );
}
